import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Save, Plus, Trash2, GripVertical, Type, Heading, Image, 
  Square, Code, Search, ListFilter, PanelLeft, ArrowUp, ArrowDown, Eye
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sidebar widget types
const WIDGET_TYPES = [
  { type: 'heading', label: 'Heading', icon: Heading },
  { type: 'text', label: 'Text Block', icon: Type },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'button', label: 'Button/Link', icon: Square },
  { type: 'html', label: 'Custom HTML', icon: Code },
  { type: 'search', label: 'Search Box', icon: Search },
  { type: 'categories', label: 'Categories', icon: ListFilter },
  { type: 'recent-posts', label: 'Recent Posts', icon: ListFilter },
];

// Preview Widget Renderer for live preview
function PreviewWidget({ widget }) {
  switch (widget.type) {
    case 'heading':
      const HeadingTag = widget.content.level || 'h3';
      const headingClasses = {
        h2: 'text-xl font-bold mb-3',
        h3: 'text-lg font-semibold mb-2',
        h4: 'text-base font-semibold mb-2'
      };
      return (
        <HeadingTag className={`${headingClasses[HeadingTag] || headingClasses.h3} text-slate-900`}>
          {widget.content.text || 'Widget Title'}
        </HeadingTag>
      );

    case 'text':
      return (
        <p className="text-sm text-slate-600 mb-4 whitespace-pre-wrap leading-relaxed">
          {widget.content.text || 'Text content goes here...'}
        </p>
      );

    case 'image':
      return widget.content.src ? (
        <div className="mb-4">
          <img src={widget.content.src} alt={widget.content.alt || ''} className="w-full rounded-lg" />
        </div>
      ) : (
        <div className="mb-4 bg-slate-200 rounded-lg h-32 flex items-center justify-center text-slate-400 text-sm">
          Image placeholder
        </div>
      );

    case 'button':
      const btnStyle = {
        backgroundColor: widget.content.bgColor || '#2563eb',
        color: widget.content.textColor || '#ffffff'
      };
      return (
        <div className="mb-4">
          <span
            className="inline-block w-full text-center px-4 py-2 rounded-lg font-medium"
            style={btnStyle}
          >
            {widget.content.text || 'Click Here'}
          </span>
        </div>
      );

    case 'html':
      return (
        <div className="mb-4 text-sm text-slate-500 bg-slate-100 p-2 rounded border border-dashed border-slate-300">
          {widget.content.html ? 'Custom HTML Block' : 'Empty HTML Block'}
        </div>
      );

    case 'search':
      return (
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={widget.content.placeholder || 'Search...'}
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm"
              disabled
            />
          </div>
        </div>
      );

    case 'categories':
      return (
        <div className="mb-4">
          {widget.content.title && (
            <h4 className="text-base font-semibold text-slate-900 mb-3">{widget.content.title}</h4>
          )}
          <ul className="space-y-2 text-sm text-slate-600">
            <li>Category 1</li>
            <li>Category 2</li>
            <li>Category 3</li>
          </ul>
        </div>
      );

    case 'recent-posts':
      return (
        <div className="mb-4">
          {widget.content.title && (
            <h4 className="text-base font-semibold text-slate-900 mb-3">{widget.content.title}</h4>
          )}
          <ul className="space-y-3 text-sm">
            <li className="text-slate-700">Sample Post Title 1<br/><span className="text-xs text-slate-400">Jan 1, 2025</span></li>
            <li className="text-slate-700">Sample Post Title 2<br/><span className="text-xs text-slate-400">Jan 2, 2025</span></li>
          </ul>
        </div>
      );

    default:
      return null;
  }
}

// Sortable Widget Editor with drag handle
function SortableWidgetEditor({ widget, onUpdate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const updateContent = (key, value) => {
    onUpdate(widget.id, {
      ...widget,
      content: { ...widget.content, [key]: value }
    });
  };

  const renderEditor = () => {
    switch (widget.type) {
      case 'heading':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              placeholder="Widget title..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Select value={widget.content.level || 'h3'} onValueChange={(val) => updateContent('level', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
                <SelectItem value="h4">H4</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'text':
        return (
          <Textarea
            value={widget.content.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            placeholder="Enter text..."
            className="bg-slate-600 border-slate-500 text-white min-h-[80px]"
          />
        );

      case 'image':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.src || ''}
              onChange={(e) => updateContent('src', e.target.value)}
              placeholder="Image URL..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Input
              value={widget.content.alt || ''}
              onChange={(e) => updateContent('alt', e.target.value)}
              placeholder="Alt text..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Input
              value={widget.content.link || ''}
              onChange={(e) => updateContent('link', e.target.value)}
              placeholder="Link URL (optional)..."
              className="bg-slate-600 border-slate-500 text-white"
            />
          </div>
        );

      case 'button':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              placeholder="Button text..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Input
              value={widget.content.url || ''}
              onChange={(e) => updateContent('url', e.target.value)}
              placeholder="URL..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <div className="flex gap-2">
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">BG:</span>
                <input
                  type="color"
                  value={widget.content.bgColor || '#2563eb'}
                  onChange={(e) => updateContent('bgColor', e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-0"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">Text:</span>
                <input
                  type="color"
                  value={widget.content.textColor || '#ffffff'}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-0"
                />
              </div>
            </div>
          </div>
        );

      case 'html':
        return (
          <Textarea
            value={widget.content.html || ''}
            onChange={(e) => updateContent('html', e.target.value)}
            placeholder="<div>Custom HTML...</div>"
            className="bg-slate-600 border-slate-500 text-white min-h-[100px] font-mono text-sm"
          />
        );

      case 'search':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.placeholder || ''}
              onChange={(e) => updateContent('placeholder', e.target.value)}
              placeholder="Search placeholder text..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <p className="text-xs text-slate-500">Displays a search input for your site</p>
          </div>
        );

      case 'categories':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              placeholder="Widget title (e.g., Categories)..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <p className="text-xs text-slate-500">Displays list of post categories</p>
          </div>
        );

      case 'recent-posts':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              placeholder="Widget title (e.g., Recent Posts)..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Select value={widget.content.count || '5'} onValueChange={(val) => updateContent('count', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                <SelectValue placeholder="Number of posts" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="3">3 posts</SelectItem>
                <SelectItem value="5">5 posts</SelectItem>
                <SelectItem value="10">10 posts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      default:
        return <p className="text-slate-400 text-sm">Unknown widget type</p>;
    }
  };

  const widgetType = WIDGET_TYPES.find(w => w.type === widget.type);
  const Icon = widgetType?.icon || Type;

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="bg-slate-700 border-slate-600">
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <div
              {...attributes}
              {...listeners}
              className="p-2 cursor-grab active:cursor-grabbing text-slate-400 hover:text-white hover:bg-slate-600 rounded"
            >
              <GripVertical className="w-4 h-4" />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium text-slate-400">{widgetType?.label || widget.type}</span>
              </div>
              {renderEditor()}
            </div>
            
            <button
              onClick={() => onDelete(widget.id)}
              className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-600 rounded"
              data-testid={`delete-widget-${widget.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function WidgetEditor({ widget, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const updateContent = (key, value) => {
    onUpdate(widget.id, {
      ...widget,
      content: { ...widget.content, [key]: value }
    });
  };

  const renderEditor = () => {
    switch (widget.type) {
      case 'heading':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              placeholder="Widget title..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Select value={widget.content.level || 'h3'} onValueChange={(val) => updateContent('level', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
                <SelectItem value="h4">H4</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'text':
        return (
          <Textarea
            value={widget.content.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            placeholder="Enter text..."
            className="bg-slate-600 border-slate-500 text-white min-h-[80px]"
          />
        );

      case 'image':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.src || ''}
              onChange={(e) => updateContent('src', e.target.value)}
              placeholder="Image URL..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Input
              value={widget.content.alt || ''}
              onChange={(e) => updateContent('alt', e.target.value)}
              placeholder="Alt text..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Input
              value={widget.content.link || ''}
              onChange={(e) => updateContent('link', e.target.value)}
              placeholder="Link URL (optional)..."
              className="bg-slate-600 border-slate-500 text-white"
            />
          </div>
        );

      case 'button':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              placeholder="Button text..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Input
              value={widget.content.url || ''}
              onChange={(e) => updateContent('url', e.target.value)}
              placeholder="URL..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <div className="flex gap-2">
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">BG:</span>
                <input
                  type="color"
                  value={widget.content.bgColor || '#2563eb'}
                  onChange={(e) => updateContent('bgColor', e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-0"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">Text:</span>
                <input
                  type="color"
                  value={widget.content.textColor || '#ffffff'}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-0"
                />
              </div>
            </div>
          </div>
        );

      case 'html':
        return (
          <Textarea
            value={widget.content.html || ''}
            onChange={(e) => updateContent('html', e.target.value)}
            placeholder="<div>Custom HTML...</div>"
            className="bg-slate-600 border-slate-500 text-white min-h-[100px] font-mono text-sm"
          />
        );

      case 'search':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.placeholder || ''}
              onChange={(e) => updateContent('placeholder', e.target.value)}
              placeholder="Search placeholder text..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <p className="text-xs text-slate-500">Displays a search input for your site</p>
          </div>
        );

      case 'categories':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              placeholder="Widget title (e.g., Categories)..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <p className="text-xs text-slate-500">Displays list of post categories</p>
          </div>
        );

      case 'recent-posts':
        return (
          <div className="space-y-2">
            <Input
              value={widget.content.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              placeholder="Widget title (e.g., Recent Posts)..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Select value={widget.content.count || '5'} onValueChange={(val) => updateContent('count', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                <SelectValue placeholder="Number of posts" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="3">3 posts</SelectItem>
                <SelectItem value="5">5 posts</SelectItem>
                <SelectItem value="10">10 posts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      default:
        return <p className="text-slate-400 text-sm">Unknown widget type</p>;
    }
  };

  const widgetType = WIDGET_TYPES.find(w => w.type === widget.type);
  const Icon = widgetType?.icon || Type;

  return (
    <Card className="bg-slate-700 border-slate-600">
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <div className="flex flex-col gap-1">
            <button
              onClick={onMoveUp}
              disabled={isFirst}
              className={`p-1 rounded ${isFirst ? 'text-slate-600' : 'text-slate-400 hover:text-white hover:bg-slate-600'}`}
            >
              <ArrowUp className="w-3 h-3" />
            </button>
            <button
              onClick={onMoveDown}
              disabled={isLast}
              className={`p-1 rounded ${isLast ? 'text-slate-600' : 'text-slate-400 hover:text-white hover:bg-slate-600'}`}
            >
              <ArrowDown className="w-3 h-3" />
            </button>
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4 text-blue-400" />
              <span className="text-xs font-medium text-slate-400">{widgetType?.label || widget.type}</span>
            </div>
            {renderEditor()}
          </div>
          
          <button
            onClick={() => onDelete(widget.id)}
            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-600 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SidebarBuilder() {
  const { token } = useAuth();
  const [widgets, setWidgets] = useState([]);
  const [settings, setSidebarSettings] = useState({
    enabled: true,
    position: 'right',
    width: 'md',
    showOnPages: true,
    showOnPosts: true,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Configure dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchSidebar();
  }, []);

  const fetchSidebar = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sidebar`);
      if (response.ok) {
        const data = await response.json();
        setWidgets(data.widgets || []);
        setSidebarSettings(data.settings || settings);
      }
    } catch (error) {
      console.log('No sidebar data yet');
    } finally {
      setLoading(false);
    }
  };

  const saveSidebar = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/sidebar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ widgets, settings })
      });

      if (response.ok) {
        toast.success('Sidebar saved!');
      } else {
        toast.error('Failed to save sidebar');
      }
    } catch (error) {
      toast.error('Error saving sidebar');
    } finally {
      setSaving(false);
    }
  };

  const addWidget = (type) => {
    const newWidget = {
      id: `widget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      content: type === 'heading' ? { text: 'Widget Title', level: 'h3' } :
               type === 'text' ? { text: '' } :
               type === 'image' ? { src: '', alt: '', link: '' } :
               type === 'button' ? { text: 'Click Here', url: '#', bgColor: '#2563eb', textColor: '#ffffff' } :
               type === 'html' ? { html: '' } :
               type === 'search' ? { placeholder: 'Search...' } :
               type === 'categories' ? { title: 'Categories' } :
               type === 'recent-posts' ? { title: 'Recent Posts', count: '5' } : {}
    };
    setWidgets([...widgets, newWidget]);
  };

  const updateWidget = (id, updatedWidget) => {
    setWidgets(widgets.map(w => w.id === id ? updatedWidget : w));
  };

  const deleteWidget = (id) => {
    setWidgets(widgets.filter(w => w.id !== id));
  };

  const moveWidget = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= widgets.length) return;
    
    const newWidgets = [...widgets];
    const [moved] = newWidgets.splice(index, 1);
    newWidgets.splice(newIndex, 0, moved);
    setWidgets(newWidgets);
  };

  // Handle drag end for dnd-kit
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setWidgets((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Get width class for preview
  const getWidthClass = () => {
    const widths = { sm: 'w-[250px]', md: 'w-[300px]', lg: 'w-[350px]' };
    return widths[settings.width] || 'w-[300px]';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-800/50 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PanelLeft className="w-6 h-6 text-blue-400" />
              <h1 className="text-xl font-bold text-white">Sidebar Builder</h1>
            </div>
            <div className="flex items-center gap-3">
              <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="preview-sidebar-btn" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-100 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-slate-900">Sidebar Preview</DialogTitle>
                  </DialogHeader>
                  <div className={`${getWidthClass()} mx-auto`}>
                    {widgets.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <PanelLeft className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No widgets to preview</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {widgets.map((widget) => (
                          <div key={widget.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                            <PreviewWidget widget={widget} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              <Button onClick={saveSidebar} disabled={saving} data-testid="save-sidebar-btn" className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Sidebar'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar Settings */}
          <div className="lg:col-span-1">
            <Card className="bg-slate-800 border-slate-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-lg">Sidebar Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={settings.enabled}
                    onChange={(e) => setSidebarSettings({ ...settings, enabled: e.target.checked })}
                    className="w-4 h-4 rounded"
                    data-testid="sidebar-enabled-checkbox"
                  />
                  <label htmlFor="enabled" className="text-sm text-slate-300">Enable Sidebar</label>
                </div>

                <div>
                  <label className="text-sm text-slate-400 block mb-2">Position</label>
                  <Select value={settings.position} onValueChange={(val) => setSidebarSettings({ ...settings, position: val })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="sidebar-position-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-slate-400 block mb-2">Width</label>
                  <Select value={settings.width} onValueChange={(val) => setSidebarSettings({ ...settings, width: val })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="sidebar-width-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="sm">Small (250px)</SelectItem>
                      <SelectItem value="md">Medium (300px)</SelectItem>
                      <SelectItem value="lg">Large (350px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-2 border-t border-slate-700">
                  <p className="text-sm text-slate-400 mb-2">Show Sidebar On:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="showOnPages"
                        checked={settings.showOnPages}
                        onChange={(e) => setSidebarSettings({ ...settings, showOnPages: e.target.checked })}
                        className="w-4 h-4 rounded"
                      />
                      <label htmlFor="showOnPages" className="text-sm text-slate-300">Pages</label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="showOnPosts"
                        checked={settings.showOnPosts}
                        onChange={(e) => setSidebarSettings({ ...settings, showOnPosts: e.target.checked })}
                        className="w-4 h-4 rounded"
                      />
                      <label htmlFor="showOnPosts" className="text-sm text-slate-300">Posts</label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Add Widget */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Add Widget</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {WIDGET_TYPES.map((widget) => (
                    <button
                      key={widget.type}
                      onClick={() => addWidget(widget.type)}
                      data-testid={`add-widget-${widget.type}`}
                      className="flex items-center gap-2 p-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-left transition-colors"
                    >
                      <widget.icon className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-slate-300">{widget.label}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Widget List */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Widgets ({widgets.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {widgets.length === 0 ? (
                  <div className="text-center py-12">
                    <PanelLeft className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No widgets yet</p>
                    <p className="text-sm text-slate-500">Add widgets from the left panel</p>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={widgets.map(w => w.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {widgets.map((widget) => (
                          <SortableWidgetEditor
                            key={widget.id}
                            widget={widget}
                            onUpdate={updateWidget}
                            onDelete={deleteWidget}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
